import type { NthWeekday, RecurrenceRule, WallClockTime, Weekday } from './types';

const WEEKDAY_SHORT: Record<Weekday, string> = {
  1: 'Mon',
  2: 'Tue',
  3: 'Wed',
  4: 'Thu',
  5: 'Fri',
  6: 'Sat',
  7: 'Sun',
};

const WEEKDAY_LONG: Record<Weekday, string> = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
};

const NTH_LABEL: Record<NthWeekday, string> = {
  1: 'first',
  2: 'second',
  3: 'third',
  4: 'fourth',
  '-1': 'last',
};

export function weekdayShort(day: Weekday): string {
  return WEEKDAY_SHORT[day];
}

export function weekdayLong(day: Weekday): string {
  return WEEKDAY_LONG[day];
}

/**
 * 12-hour time, formatted by hand rather than through Intl so that output is
 * identical regardless of the host locale — the calendar and the rule summary
 * must never disagree about what "18:00" says.
 */
export function formatTime(time: WallClockTime): string {
  const [rawHour = '0', rawMinute = '00'] = time.split(':');
  const hour24 = Number(rawHour);
  const suffix = hour24 < 12 ? 'am' : 'pm';
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${hour12}:${rawMinute}${suffix}`;
}

export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  const hourPart = `${hours} hr`;
  return rest === 0 ? hourPart : `${hourPart} ${rest} min`;
}

function joinWeekdays(days: readonly Weekday[]): string {
  return [...new Set(days)]
    .sort((a, b) => a - b)
    .map(weekdayShort)
    .join(', ');
}

/** Human phrasing for a rule on its own, e.g. `Every 2 weeks on Tue, Thu`. */
export function formatRecurrence(rule: RecurrenceRule): string {
  switch (rule.freq) {
    case 'once':
      return 'One time only';
    case 'daily':
      return 'Every day';
    case 'weekly':
      return rule.byWeekday.length === 0
        ? 'Weekly (no days selected)'
        : `Weekly on ${joinWeekdays(rule.byWeekday)}`;
    case 'biweekly':
      return rule.byWeekday.length === 0
        ? 'Every 2 weeks (no days selected)'
        : `Every 2 weeks on ${joinWeekdays(rule.byWeekday)}`;
    case 'monthly':
      return rule.mode === 'byMonthDay'
        ? `Monthly on day ${rule.monthDay}`
        : `Monthly on the ${NTH_LABEL[rule.nth]} ${weekdayLong(rule.weekday)}`;
    default: {
      const exhaustive: never = rule;
      throw new Error(`Unsupported rule: ${JSON.stringify(exhaustive)}`);
    }
  }
}

export interface SeriesSummaryInput {
  readonly recurrence: RecurrenceRule;
  readonly startTime: WallClockTime;
  readonly locationName?: string;
}

/**
 * The one-line description shown under the class form and in the class list,
 * e.g. `Every 2 weeks on Tue, Thu at 6:00pm — Studio A`.
 */
export function formatSeriesSummary(input: SeriesSummaryInput): string {
  const base = `${formatRecurrence(input.recurrence)} at ${formatTime(input.startTime)}`;
  return input.locationName === undefined || input.locationName.length === 0
    ? base
    : `${base} — ${input.locationName}`;
}
